import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

function TorusKnot() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.z = 5;

    // Geometry
    const geo = new THREE.TorusKnotGeometry(1.4, 0.4, 220, 32, 2, 3);

    // Solid mesh
    const mat = new THREE.MeshStandardMaterial({ color: 0x7c3aed, metalness: 0.75, roughness: 0.08 });
    const knot = new THREE.Mesh(geo, mat);
    scene.add(knot);

    // Wireframe overlay
    const wireMat = new THREE.MeshBasicMaterial({ color: 0xa78bfa, wireframe: true, opacity: 0.12, transparent: true });
    const wire = new THREE.Mesh(geo, wireMat);
    scene.add(wire);

    // Lights
    scene.add(new THREE.AmbientLight(0x4a1d96, 0.5));

    const light1 = new THREE.PointLight(0xa78bfa, 5, 20);
    light1.position.set(3, 3, 3);
    scene.add(light1);

    const light2 = new THREE.PointLight(0x7c3aed, 4, 20);
    light2.position.set(-3, -2, 2);
    scene.add(light2);

    const light3 = new THREE.PointLight(0xc4b5fd, 2.5, 15);
    light3.position.set(0, -3, 3);
    scene.add(light3);

    // Mouse
    const onMouse = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouse);

    // Resize
    const onResize = () => {
      const nw = canvas.clientWidth;
      const nh = canvas.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    let t = 0;
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.008;

      knot.rotation.x += (mouse.current.y * 0.28 - knot.rotation.x) * 0.05;
      knot.rotation.y += (mouse.current.x * 0.28 - knot.rotation.y) * 0.05;
      knot.rotation.z += 0.005;
      wire.rotation.copy(knot.rotation);

      light1.position.x = Math.sin(t * 0.7) * 4;
      light1.position.y = Math.cos(t * 0.5) * 4;
      light2.position.x = Math.cos(t * 0.6) * 4;
      light2.position.y = Math.sin(t * 0.8) * 3;

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      geo.dispose();
      mat.dispose();
      wireMat.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

export default TorusKnot;
